/* eslint-disable @typescript-eslint/no-explicit-any */
import { Structure, StructureQuery } from 'molstar/lib/mol-model/structure';
import { Loci } from 'molstar/lib/mol-model/structure/structure/element/loci';
import { presetStaticComponent } from 'molstar/lib/mol-plugin-state/builder/structure/representation-preset';
import { BuiltInTrajectoryFormat } from 'molstar/lib/mol-plugin-state/formats/trajectory';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { StructureSelectionQuery } from 'molstar/lib/mol-plugin-state/helpers/structure-selection-query';
import { StructureRef } from 'molstar/lib/mol-plugin-state/manager/structure/hierarchy-state';
import { PluginStateObject } from 'molstar/lib/mol-plugin-state/objects';
import { PluginUIContext } from 'molstar/lib/mol-plugin-ui/context';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { MolScriptBuilder } from 'molstar/lib/mol-script/language/builder';
import { StateObjectRef, StateObjectSelector } from 'molstar/lib/mol-state';
import { Color } from 'molstar/lib/mol-util/color';
import { ApoHoloSiteDto } from '../../models/ApoHoloSiteDto';
import { PocketResiduesResult } from '../../models/PocketResiduesResult';
import { StructureData } from '../../models/StructureData';
import {
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from './Controls/StructureRepresentationComponent';

/**
 * A live reference to one loaded structure in the Molstar state tree.
 *
 * @property data            - Root data node selector; used for `ToggleVisibility` commands.
 * @property trajectory      - Parsed trajectory selector.
 * @property isVisible       - Current visibility tracked locally (mirrors Molstar state).
 * @property site            - Originating site descriptor; links back to the results table row.
 * @property pocketLoaded    - `true` once the pocket/chain components have been explicitly
 *                             built (they are created lazily on first representation change).
 * @property ligandLoaded    - `true` once the ligand component has been explicitly built.
 * @property molStructureName - Molstar internal `sourceRef` used to look up the
 *                              `StructureRef` in the hierarchy manager.
 */
export type StructureReference = {
   data: StateObjectSelector;
   trajectory: StateObjectSelector<PluginStateObject.Molecule.Trajectory>;
   isVisible: boolean;
   site: ApoHoloSiteDto;
   pocketLoaded: boolean;
   ligandLoaded: boolean;
   molStructureName?: string;
};

/**
 * Thin wrapper around Molstar's `PluginUIContext` that provides the domain-specific
 * operations needed by AHoJ:
 * - Batch and sequential structure loading with duplicate-load guards
 * - Per-component visibility, representation, and color control
 * - Residue-level and pocket-level camera focus
 * - Water toggle
 * - UniProt position hover / selection synchronization
 *
 * The wrapper is created inside `MolstarComponent.init()` and exposed via
 * `wrapperRef` so that the event-bus subscriptions can call into it.
 */
export class AhojMolstarWrapper {
   plugin: PluginUIContext;
   downloadedData: StructureData[] = [];
   format: BuiltInTrajectoryFormat = 'mmcif';
   structureReferences: Map<string, StructureReference> = new Map();
   apoHoloSites: ApoHoloSiteDto[] = [];
   loading = false;
   private loadingLabels: Set<string> = new Set();

   constructor(plugin: PluginUIContext) {
      this.plugin = plugin;
   }

   /**
    * Returns `true` when the given component label matches `componentType`.
    * Used to filter the component list of a `StructureRef` to only the intended target
    * (e.g. only the `'pocket'` component, not `'chain'` or `'ligand'`).
    */
   private componentMatchesType(label: string | undefined, componentType: StructureComponentType): boolean {
      if (!label) return false;
      return label.toLowerCase() === componentType;
   }

   /**
    * Downloads and loads multiple structures in parallel (download phase), then applies
    * them to the Molstar state tree sequentially with small `requestAnimationFrame` yields
    * between each to keep the browser responsive.
    */
   public async loadStructures(sites: ApoHoloSiteDto[]): Promise<void> {
      const downloadResults = await Promise.all(
         sites.map(async (site) => {
            try {
               return await this.downloadStructureData(site);
            } catch (error) {
               return null;
            }
         }),
      );
      const downloaded = downloadResults.filter((d): d is StructureData => d !== null);

      for (const structureData of downloaded) {
         try {
            await this.loadStructureFromDownloadedData(structureData);
         } catch (error) {}
         await new Promise((resolve) => setTimeout(resolve, 10));
      }
   }

   /** Convenience wrapper: downloads a single site and immediately loads it into Molstar. */
   public async loadStructure(site: ApoHoloSiteDto): Promise<void> {
      const structureData = await this.downloadStructureData(site);
      await this.loadStructureFromDownloadedData(structureData);
   }

   /**
    * Fetches the mmCIF file for `site` from the backend. The filename portion of the URL
    * (`url.split('/').pop()`) becomes the `label` used as the key in `structureReferences`.
    * The optional `signal` allows the caller to abort the request (e.g. on component unmount).
    */
   public async downloadStructureData(site: ApoHoloSiteDto, signal?: AbortSignal): Promise<StructureData> {
      const url = String(site.url);
      if (!url) {
         throw new Error(`No URL for site ${site.id}`);
      }
      const response = await fetch(url, { signal });

      if (!response.ok) {
         throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.text();
      const structureData: StructureData = {
         label: url.split('/').pop() || 'Structure',
         cifData: data,
         site: site,
      };
      this.downloadedData.push(structureData);
      return structureData;
   }

   /**
    * Applies previously downloaded mmCIF data to the Molstar state tree.
    *
    * Steps:
    * 1. Checks `structureReferences` and `loadingLabels` to prevent duplicate loads when
    *    multiple rows reference the same file.
    * 2. Creates a raw-data node, yields a frame (via `requestAnimationFrame`), then parses
    *    the trajectory and yields again before the heavy CPU work of applying the preset.
    * 3. Registers the loaded structure in `structureReferences` with `pocketLoaded: false`
    *    and `ligandLoaded: false` (components are created lazily on first repr change).
    */
   async loadStructureFromDownloadedData(structureData: StructureData): Promise<void> {
      try {
         const plugin = this.plugin;

         let data: StateObjectSelector;
         const cifData: string = structureData.cifData;
         const label: string = structureData.label;

         // Guard: if this file is already loaded or is currently being loaded, skip to avoid
         // creating a second orphaned Molstar node (happens when multiple rows share the same URL).
         if (this.structureReferences.has(label) || this.loadingLabels.has(label)) {
            return;
         }
         this.loadingLabels.add(label);

         try {
            data = await plugin.builders.data.rawData({
               data: cifData,
               label: label,
            });
         } catch (error) {
            this.loadingLabels.delete(label);
            throw new Error(`Failed to create data node: ${error instanceof Error ? error.message : 'Unknown error'}`);
         }

         // Yield after data node creation so browser can process interactions
         await new Promise((resolve) => requestAnimationFrame(resolve));

         let trajectory: StateObjectSelector<PluginStateObject.Molecule.Trajectory>;
         try {
            trajectory = await plugin.builders.structure.parseTrajectory(data, this.format);
         } catch (error) {
            throw new Error(`Failed to parse trajectory: ${error instanceof Error ? error.message : 'Unknown error'}`);
         }

         // Yield after trajectory parsing (heaviest CPU phase coming next)
         await new Promise((resolve) => requestAnimationFrame(resolve));

         try {
            const hierarchy = await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');

            const name = hierarchy?.structure?.cell?.sourceRef;

            this.structureReferences.set(label, {
               data,
               trajectory,
               isVisible: true,
               site: structureData.site,
               pocketLoaded: false,
               ligandLoaded: false,
               molStructureName: name,
            });
            this.loadingLabels.delete(label);
         } catch (error) {
            this.loadingLabels.delete(label);
            throw new Error(
               `Failed to apply structure preset: ${error instanceof Error ? error.message : 'Unknown error'}`,
            );
         }
      } catch (error) {
         console.error('loadStructureFromData failed:', error);
         return Promise.reject(error);
      }
   }

   /** Shows or hides all currently loaded structures of the given type. */
   public async toggleVisibility(
      type: 'apo' | 'holo' | 'unobserved' | 'query_pocket' | 'alphafold',
      visible: boolean,
   ): Promise<void> {
      for (const [, value] of Array.from(this.structureReferences)) {
         if (value.site.type === type && value.isVisible !== visible) {
            value.isVisible = visible;

            await PluginCommands.State.ToggleVisibility(this.plugin, {
               state: this.plugin.state.data,
               ref: value.data.ref,
            });
         }
      }
   }

   /** Resolves the Molstar `Structure` data object for the given label. */
   private getStructureDataByLabel(label: string): Structure | undefined {
      const ref = this.structureReferences.get(label);
      if (!ref) return undefined;

      const structureRef = this.getStructureRef(label);
      const structureData = structureRef?.cell.obj?.data;
      return structureData;
   }

   /**
    * Selects or deselects the entire structure in Molstar's interactivity manager.
    * Selection moves the camera to frame the structure when `value` is `true`.
    *
    * Note: direct `focusLoci` camera zoom is intentionally commented out; the current
    * behavior uses `lociSelects.select` which triggers Molstar's built-in focus behavior
    * without being as disorienting as an abrupt zoom.
    */
   public toggleFocus(label: string, value: boolean): void {
      const structureData = this.getStructureDataByLabel(label);
      if (!structureData) {
         return;
      }

      const loci: Loci = Structure.toStructureElementLoci(structureData);

      if (value) {
         // this.plugin.managers.camera.focusLoci(loci);
         this.plugin.managers.interactivity.lociSelects.select({ loci: loci });
      }
   }

   /** Clears all Molstar interactivity selections. */
   public deselectAll(): void {
      this.plugin.managers.interactivity.lociSelects.deselectAll();
   }

   /**
    * Builds a MolScript query for the pocket's auth_seq_id residues and focuses or
    * deselects them in the camera/interactivity managers.
    */
   public focusPocketResidues(label: string, residueIds: number[], focus: boolean): void {
      const structureData = this.getStructureDataByLabel(label);
      if (!structureData) return;

      const pocketExprs = MolScriptBuilder.struct.generator.atomGroups({
         'residue-test': MolScriptBuilder.core.set.has([
            MolScriptBuilder.set(...residueIds),
            MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(),
         ]),
      });

      const pocketQuery = StructureSelectionQuery('pocket-focus', pocketExprs);
      const loci = StructureQuery.loci(pocketQuery.query, structureData);

      if (focus) {
         this.plugin.managers.camera.focusLoci(loci);
         this.plugin.managers.interactivity.lociSelects.select({ loci });
      } else {
         this.plugin.managers.interactivity.lociSelects.deselect({ loci });
      }
   }

   /**
    * Focuses the camera on a single residue identified by its auth_seq_id.
    * Used when the user clicks on a residue in the RCSB sequence viewer.
    */
   public focusSingleResidue(label: string, residueSeqId: number, focus: boolean): void {
      const structureData = this.getStructureDataByLabel(label);
      if (!structureData) return;

      const residueExprs = MolScriptBuilder.struct.generator.atomGroups({
         'residue-test': MolScriptBuilder.core.set.has([
            MolScriptBuilder.set(residueSeqId),
            MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(),
         ]),
      });

      const residueQuery = StructureSelectionQuery('residue-focus', residueExprs);
      const loci = StructureQuery.loci(residueQuery.query, structureData);

      if (focus) {
         this.plugin.managers.camera.focusLoci(loci);
         this.plugin.managers.interactivity.lociSelects.select({ loci });
      } else {
         this.plugin.managers.interactivity.lociSelects.deselect({ loci });
      }
   }

   /**
    * Selects atoms at the given UniProt position across ALL loaded structures.
    * Iterates over every structure reference and selects matching residues without
    * clearing previous selections (`false` for the addToSelection param) so positions
    * from multiple chains are highlighted simultaneously.
    */
   public selectUniprotPosition(uniprotPos: number | null): void {
      this.plugin.managers.interactivity.lociSelects.deselectAll();
      if (uniprotPos === null) return;

      const residueExprs = MolScriptBuilder.struct.generator.atomGroups({
         'residue-test': MolScriptBuilder.core.set.has([
            MolScriptBuilder.set(uniprotPos),
            MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(),
         ]),
      });
      const residueQuery = StructureSelectionQuery('uniprot-click', residueExprs);

      for (const [label] of Array.from(this.structureReferences)) {
         const structureData = this.getStructureDataByLabel(label);
         if (!structureData) continue;
         const loci = StructureQuery.loci(residueQuery.query, structureData);
         if (!Loci.isEmpty(loci)) {
            this.plugin.managers.interactivity.lociSelects.select({ loci }, false);
         }
      }
   }

   /**
    * Temporarily highlights (hover) atoms at the given UniProt position across all
    * loaded structures. Clearing happens by passing `null` as `uniprotPos`.
    */
   public highlightUniprotPosition(uniprotPos: number | null): void {
      this.plugin.managers.interactivity.lociHighlights.clearHighlights();
      if (uniprotPos === null) return;

      const residueExprs = MolScriptBuilder.struct.generator.atomGroups({
         'residue-test': MolScriptBuilder.core.set.has([
            MolScriptBuilder.set(uniprotPos),
            MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(),
         ]),
      });
      const residueQuery = StructureSelectionQuery('uniprot-hover', residueExprs);

      for (const [label] of Array.from(this.structureReferences)) {
         const structureData = this.getStructureDataByLabel(label);
         if (!structureData) continue;
         const loci = StructureQuery.loci(residueQuery.query, structureData);
         if (!Loci.isEmpty(loci)) {
            this.plugin.managers.interactivity.lociHighlights.highlight({ loci }, false);
         }
      }
   }

   /**
    * Finds the label in structureReferences for a given Structure instance (e.g. from a click loci).
    */
   public getLabelFromStructure(structure: Structure): string | undefined {
      for (const [label] of Array.from(this.structureReferences)) {
         const structureRef = this.getStructureRef(label);
         const root = structureRef?.cell.obj?.data;
         if (!root) continue;
         // Walk up the parent chain: clicked loci may be a sub-structure
         let s: Structure | undefined = structure;
         while (s) {
            if (s === root) return label;
            s = s.parent;
         }
      }
      return undefined;
   }

   /**
    * Resolves the StructureRef from the hierarchy manager using the stored preset result.
    */
   public getStructureRef(label: string): StructureRef | undefined {
      const ref = this.structureReferences.get(label);
      if (!ref) return undefined;

      return this.plugin.managers.structure.hierarchy.current.structures.find(
         (s) => s.cell.sourceRef === ref.molStructureName,
      );
   }

   /**
    * Toggles a structure's visibility in the Molstar state tree.
    *
    * If `visible` is `true` and the structure is not yet loaded, it fetches and loads it
    * on demand, then calls `onLoaded` so the caller can apply the correct representation.
    * If the structure is already loaded, it just toggles the cell's hidden flag.
    */
   public async toggleCellVisibility(
      label: string,
      visible: boolean,
      onLoaded?: (site: ApoHoloSiteDto) => Promise<void>,
   ): Promise<void> {
      const structureRef = this.structureReferences.get(label);

      if (!structureRef) {
         if (visible) {
            const site = this.findSiteByLabel(label);
            if (site) {
               await this.loadStructure(site);
               await onLoaded?.(site);
            }
         }
         return;
      }

      if (structureRef.isVisible !== visible) {
         structureRef.isVisible = visible;

         const liveRef = this.getStructureRef(label);
         const ref = liveRef?.cell.transform.ref ?? structureRef.data.ref;

         await PluginCommands.State.ToggleVisibility(this.plugin, {
            state: this.plugin.state.data,
            ref,
         });
      }
   }

   /**
    * Replaces the entire structure representation with distinct pocket and chain
    * components.
    *
    * This is called the FIRST time a pocket representation change is requested
    * (`pocketLoaded === false`). The workflow is:
    * 1. Remove all existing default-preset components ('Polymer', 'Ligand', etc.).
    * 2. Create a 'pocket' component using a MolScript selection of the pocket residue
    *    auth_seq_ids.
    * 3. Create a 'chain' component from the complement (all atoms except pocket residues).
    * 4. Re-add the 'ligand' component with ball-and-stick to preserve ligand visibility.
    *
    * After this call, the structure has three named components: 'pocket', 'chain',
    * 'ligand' — which are targeted by subsequent `changeRepresentation` / `changeColor`
    * operations via `componentMatchesType`.
    */
   private async changeStructurePocketRepresentation(
      label: string,
      pocketResidues: PocketResiduesResult,
      type: Representation,
   ): Promise<void> {
      const structureRef = this.getStructureRef(label);
      const structureData = this.getStructureDataByLabel(label);
      if (!structureRef || !structureData) return;

      const residueIds = pocketResidues.residues.map((r) => parseInt(r)).filter((r) => !isNaN(r));
      const b = this.plugin.builders.structure;

      const pocketExprs = MolScriptBuilder.struct.generator.atomGroups({
         'residue-test': MolScriptBuilder.core.set.has([
            MolScriptBuilder.set(...residueIds),
            MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(),
         ]),
      });

      const pocketQuery = StructureSelectionQuery('pocket', pocketExprs);

      const restQuery = StructureSelectionQuery(
         'chain',
         MolScriptBuilder.struct.modifier.exceptBy({
            0: MolScriptBuilder.struct.generator.all(),
            by: pocketExprs,
         }),
      );

      // 2. Clear existing representations to avoid overlapping visuals
      // This removes 'Polymer', 'Ligand', etc., created by the default preset
      await this.plugin.managers.structure.component.removeRepresentations(structureRef.components);

      const pocketComp = structureRef.components.find((c) => c.cell.obj?.label === 'pocket');
      let pocketRef: StateObjectRef | undefined;

      if (pocketComp) {
         pocketRef = pocketComp.cell;
      } else {
         const pocketSelector = await b.tryCreateComponentFromSelection(structureRef.cell, pocketQuery, 'pocket');
         pocketRef = pocketSelector;
      }

      if (pocketRef) {
         await b.representation.addRepresentation(pocketRef, {
            type: type,
            color: 'element-symbol',
         });
      }

      const restComp = structureRef.components.find((c) => c.cell.obj?.label === 'chain');
      let restRef: StateObjectRef | undefined;
      if (restComp) {
         restRef = restComp.cell;
      } else {
         restRef = await b.tryCreateComponentFromSelection(structureRef.cell, restQuery, 'chain');
      }

      if (restRef) {
         await b.representation.addRepresentation(restRef, {
            type: defaultRestRepresentation,
         });
      }

      const ligandComponent = structureRef.components.find((c) =>
         this.componentMatchesType(c.cell.obj?.label, 'ligand'),
      );
      let ligandRef = ligandComponent?.cell;
      if (!ligandRef) {
         const ligandSelector = await presetStaticComponent(this.plugin, structureRef.cell, 'ligand');
         ligandRef = ligandSelector?.cell;
      }
      if (ligandRef) {
         await b.representation.addRepresentation(ligandRef, {
            type: 'ball-and-stick',
            color: 'element-symbol',
         });
      }
   }

   /**
    * Replaces only the ligand component's representation.
    * Called the first time a ligand representation change is requested
    * (`ligandLoaded === false`) or when the global ligand repr is changed.
    */
   private async changeStructureLigandRepresentation(label: string, type: Representation): Promise<void> {
      const structureRef = this.getStructureRef(label);
      const structureData = this.getStructureDataByLabel(label);
      if (!structureRef || !structureData) return;

      await this.plugin.managers.structure.component.removeRepresentations(
         structureRef.components.filter((c) => this.componentMatchesType(c.cell.obj?.label, 'ligand')),
      );

      const ligandComponent = structureRef.components.find((c) =>
         this.componentMatchesType(c.cell.obj?.label, 'ligand'),
      );
      let ligandRef = ligandComponent?.cell;

      if (!ligandRef) {
         const ligandSelector = await presetStaticComponent(this.plugin, structureRef.cell, 'ligand');
         ligandRef = ligandSelector?.cell;
      }

      if (ligandRef) {
         await this.plugin.builders.structure.representation.addRepresentation(ligandRef, {
            type,
            color: 'element-symbol',
         });
      }
   }

   /** Searches `apoHoloSites` for the site whose URL ends with `label`. */
   private findSiteByLabel(label: string): ApoHoloSiteDto | undefined {
      for (const site of this.apoHoloSites) {
         const filename = site.url.split('/').pop() || '';
         if (filename === label) {
            return site;
         }
      }
      return undefined;
   }

   /**
    * Applies a representation change globally across all loaded structures.
    *
    * Skips structures whose pocket/ligand components haven't been built yet
    * (to avoid applying a `'pocket'` change to the default generic component).
    * When `structureComponentType` is `'chain'` but no pocket has been loaded,
    * the effective type is set to `undefined` so the whole structure is updated
    * instead (avoids visual inconsistency where only the default polymer gets styled).
    */
   public async changeRepresentation(
      type: Representation,
      structureComponentType?: StructureComponentType,
   ): Promise<void> {
      const plugin = this.plugin;
      const structures = plugin.managers.structure.hierarchy.current.structures;
      let tasks: Promise<unknown>[] = [];

      for (const structureRef of structures) {
         const cachedRef = Array.from(this.structureReferences.values()).find(
            (r) => r.molStructureName === structureRef.cell.sourceRef,
         );
         const pocketLoaded = cachedRef?.pocketLoaded ?? false;
         const ligandLoaded = cachedRef?.ligandLoaded ?? false;
         let effective: StructureComponentType | undefined = structureComponentType;
         if (!pocketLoaded) {
            if (effective === 'pocket') continue;
            if (effective === 'chain') effective = undefined;
         }
         if (!ligandLoaded && effective === 'ligand') continue;
         tasks = this.getRepresentationUpdateTasksByComponentType(structureRef, type, tasks, effective);
      }
      await Promise.all(tasks);
   }

   /** Applies a color change globally across all loaded structures. Same pocket/ligand load guards as `changeRepresentation`. */
   public async changeColor(color: string, structureComponentType?: StructureComponentType): Promise<void> {
      const plugin = this.plugin;
      const structures = plugin.managers.structure.hierarchy.current.structures;
      let tasks: Promise<unknown>[] = [];

      for (const structureRef of structures) {
         const cachedRef = Array.from(this.structureReferences.values()).find(
            (r) => r.molStructureName === structureRef.cell.sourceRef,
         );
         const pocketLoaded = cachedRef?.pocketLoaded ?? false;
         const ligandLoaded = cachedRef?.ligandLoaded ?? false;
         let effective: StructureComponentType | undefined = structureComponentType;
         if (!pocketLoaded) {
            if (effective === 'pocket') continue;
            if (effective === 'chain') effective = undefined;
         }
         if (!ligandLoaded && effective === 'ligand') continue;
         tasks = this.getColorUpdateTasksByComponentType(structureRef, color, tasks, effective);
      }
      await Promise.all(tasks);
   }

   /**
    * The main per-row update entry point. Handles the representation AND color update
    * for one structure, with special first-time initialization logic for pocket and
    * ligand components.
    *
    * Tricky ordering rules:
    * - Representation and color updates MUST run sequentially because
    *   `updateRepresentationsTheme` reads + commits the current params snapshot;
    *   running both concurrently can cause the color task to overwrite the just-applied
    *   representation type.
    * - When `alpha` is provided, it is applied atomically inside
    *   `updateColorAndAlphaByComponentType` so the alpha cannot be clobbered by a
    *   subsequent theme rebuild resetting it to 1.0.
    * - For a first-time `'pocket'` change (`pocketLoaded === false`),
    *   `changeStructurePocketRepresentation` builds the named components before any
    *   color/alpha is applied, then marks `pocketLoaded = true`.
    * - When `componentType` is `'chain'` but no pocket has been loaded, the type is
    *   widened to `undefined` to update the whole default structure.
    */
   public updateByComponentType = async (
      structureLabel: string,
      type: Representation,
      color: string,
      pocketResidues: PocketResiduesResult,
      componentType?: StructureComponentType,
      representationChanged = true,
      colorChanged = true,
      alpha?: number,
   ): Promise<void> => {
      const structureReference = this.structureReferences.get(structureLabel);
      if (!structureReference) return;

      const pocketLoaded = structureReference.pocketLoaded;
      const ligandLoaded = structureReference.ligandLoaded;
      if (componentType === 'pocket' && !pocketLoaded) {
         await this.changeStructurePocketRepresentation(structureLabel, pocketResidues, type);
         structureReference.pocketLoaded = true;
         // Apply color after pocket components are created
         if (colorChanged) {
            if (alpha !== undefined) {
               await this.updateColorAndAlphaByComponentType(structureLabel, color, alpha, componentType);
            } else {
               const structureRef = this.getStructureRef(structureLabel);
               if (structureRef) {
                  await Promise.all(
                     this.getColorUpdateTasksByComponentType(
                        structureRef,
                        color,
                        [] as Promise<unknown>[],
                        componentType,
                     ),
                  );
               }
            }
         }
         return;
      }

      if (componentType === 'ligand' && !ligandLoaded) {
         await this.changeStructureLigandRepresentation(structureLabel, type);
         structureReference.ligandLoaded = true;
         if (colorChanged) {
            if (alpha !== undefined) {
               await this.updateColorAndAlphaByComponentType(structureLabel, color, alpha, componentType);
            } else {
               const structureRef = this.getStructureRef(structureLabel);
               if (structureRef) {
                  await Promise.all(
                     this.getColorUpdateTasksByComponentType(
                        structureRef,
                        color,
                        [] as Promise<unknown>[],
                        componentType,
                     ),
                  );
               }
            }
         }
         return;
      }

      if (componentType === 'chain' && !pocketLoaded) {
         componentType = undefined; // If pocket not loaded, update whole structure to avoid visual inconsistencies
      }

      const structureRef = this.getStructureRef(structureLabel);
      if (!structureRef) return;

      // Representation and color updates MUST run sequentially.
      // updateRepresentationsTheme reads the current state snapshot (including repr type)
      // and commits it back. If both run concurrently the color task can overwrite
      // the just-applied representation type, causing the visible revert.
      if (representationChanged) {
         const reprTasks: Promise<unknown>[] = [];
         this.getRepresentationUpdateTasksByComponentType(structureRef, type, reprTasks, componentType);
         await Promise.all(reprTasks);
      }
      if (colorChanged) {
         // When a target alpha is provided, set color and alpha in a single atomic
         // builder commit so they are never applied separately and cannot race.
         if (alpha !== undefined) {
            await this.updateColorAndAlphaByComponentType(structureLabel, color, alpha, componentType);
         } else {
            const colorTasks: Promise<unknown>[] = [];
            this.getColorUpdateTasksByComponentType(structureRef, color, colorTasks, componentType);
            await Promise.all(colorTasks);
         }
      }
   };

   /**
    * Updates color and alpha atomically by running them sequentially within the same
    * async function. updateRepresentationsTheme correctly triggers Molstar's color theme
    * re-evaluation pipeline (which a raw cell param patch does not). setStructureTransparency
    * is then awaited immediately after so the alpha is applied once the color commit has
    * fully resolved — no race is possible since both are awaited in order.
    */
   private updateColorAndAlphaByComponentType = async (
      structureLabel: string,
      color: string,
      alpha: number,
      componentType?: StructureComponentType,
   ): Promise<void> => {
      const structureRef = this.getStructureRef(structureLabel);
      if (!structureRef) return;

      const components =
         componentType === undefined
            ? structureRef.components
            : structureRef.components.filter((c) => this.componentMatchesType(c.cell.obj?.label, componentType));

      if (components.length > 0) {
         await (this.plugin.managers.structure.component.updateRepresentationsTheme(components, {
            color: 'uniform',
            colorParams: { value: Color.fromHexStyle(color) },
         }) ?? Promise.resolve());
      }

      // Alpha must be applied AFTER the color commit resolves. updateRepresentationsTheme
      // rebuilds the representation which resets alpha to 1.0; awaiting it first guarantees
      // setStructureTransparency wins with no further overwrites.
      await this.setStructureTransparency(structureLabel, alpha, componentType);
   };

   /**
    * Collects Molstar state-tree update tasks for changing the representation type of
    * the matching component(s). Returns the same `tasks` array so calls can be chained.
    * All tasks are committed via `Promise.all(tasks)` by the caller.
    */
   private getRepresentationUpdateTasksByComponentType = (
      structureRef: StructureRef,
      type: Representation,
      tasks: Promise<unknown>[],
      componentType?: StructureComponentType,
   ): Promise<unknown>[] => {
      for (const component of structureRef.components) {
         const currentComponentLabel = component.cell.obj?.label;

         if (componentType !== undefined && !this.componentMatchesType(currentComponentLabel, componentType)) {
            continue;
         }

         for (const representation of component.representations) {
            const params = createStructureRepresentationParams(this.plugin, void 0, { type });
            tasks.push(this.plugin.build().to(representation.cell).update(params).commit());
         }
      }
      return tasks;
   };

   /**
    * Collects Molstar state-tree update tasks for changing the color of the matching
    * component(s) using `updateRepresentationsTheme`. Returns the same `tasks` array.
    */
   private getColorUpdateTasksByComponentType = (
      structureRef: StructureRef,
      color: string,
      tasks: Promise<unknown>[],
      componentType?: StructureComponentType,
   ): Promise<unknown>[] => {
      const components =
         componentType === undefined
            ? structureRef.components
            : structureRef.components.filter((c) => this.componentMatchesType(c.cell.obj?.label, componentType));

      if (components.length > 0) {
         tasks.push(
            this.plugin.managers.structure.component.updateRepresentationsTheme(components, {
               color: 'uniform',
               colorParams: { value: Color.fromHexStyle(color) },
            }) ?? Promise.resolve(),
         );
      }
      return tasks;
   };

   /**
    * Sets the render alpha (transparency) of the given structure's component(s) by
    * patching each representation cell's `type.params.alpha` field directly.
    * Uses Molstar's low-level `.build().to().update().commit()` pipeline to avoid
    * triggering a full theme rebuild (which is what `updateRepresentationsTheme` does
    * and which would reset the alpha back to 1.0).
    */
   public async setStructureTransparency(
      label: string,
      alpha: number,
      componentType?: StructureComponentType,
   ): Promise<void> {
      const structureRef = this.getStructureRef(label);
      if (!structureRef) return;

      const components = componentType
         ? structureRef.components.filter((c) => this.componentMatchesType(c.cell.obj?.label, componentType))
         : structureRef.components;

      if (components.length === 0) {
         return;
      }

      const tasks: Promise<unknown>[] = [];
      for (const component of components) {
         for (const representation of component.representations) {
            tasks.push(
               this.plugin
                  .build()
                  .to(representation.cell)
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
                  .update((old: any) => ({
                     ...old,
                     // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                     type: { ...old.type, params: { ...old.type.params, alpha } },
                  }))
                  .commit(),
            );
         }
      }
      await Promise.all(tasks);
   }

   /**
    * Shows or hides water molecules across all currently visible structures.
    *
    * If a 'Water' component already exists, toggles its visibility.
    * If `show` is `true` and no water component exists, creates one by:
    * 1. Trying `presetStaticComponent` with the built-in 'water' selector.
    * 2. Falling back to a manual MolScript query matching common water residue names
    *    (HOH, WAT, etc.) for structures where Molstar's preset doesn't find water.
    * Skips hidden structures to avoid unexpectedly showing water for invisible rows.
    */
   public async toggleWater(show: boolean): Promise<void> {
      for (const structureRef of this.plugin.managers.structure.hierarchy.current.structures) {
         const cachedRef = Array.from(this.structureReferences.values()).find(
            (r) => r.molStructureName === structureRef.cell.sourceRef,
         );
         if (cachedRef && !cachedRef.isVisible) continue;
         const waterComp = structureRef.components.find((c) => c.cell.obj?.label?.toLowerCase() === 'water');

         if (waterComp) {
            const isHidden = waterComp.cell.state.isHidden ?? false;
            if (show === isHidden) {
               await PluginCommands.State.ToggleVisibility(this.plugin, {
                  state: this.plugin.state.data,
                  ref: waterComp.cell.transform.ref,
               });
            }
         } else if (show) {
            const b = this.plugin.builders.structure;

            const cell = structureRef.cell;

            let waterRef = await presetStaticComponent(this.plugin, cell, 'water');

            if (!waterRef) {
               const waterByNameExpr = MolScriptBuilder.struct.generator.atomGroups({
                  'residue-test': MolScriptBuilder.core.set.has([
                     MolScriptBuilder.set('HOH', 'WAT', 'DOD', 'H2O', 'HH0', 'OHH', 'SOL'),
                     MolScriptBuilder.struct.atomProperty.macromolecular.label_comp_id(),
                  ]),
               });
               const waterQuery = StructureSelectionQuery('water', waterByNameExpr);
               waterRef = await b.tryCreateComponentFromSelection(cell, waterQuery, 'water');
            }

            if (waterRef) {
               await b.representation.addRepresentation(waterRef, {
                  type: 'ball-and-stick',
                  typeParams: { alpha: 0.6 },
                  color: 'element-symbol',
               });
            }
         }
      }
   }
}
